import axios from 'axios';
import React, { useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import {
    formatHoldQty,
    hasHoldQty,
    holdApiMessage,
    isHoldQtyTyping,
    toHoldQty,
    validateHoldQty
} from 'helpers/holdQty';

const HoldReasonInput = ({ holdReasons = [], formData, setFormData, dataObj }) => {
    const [showOptions, setShowOptions] = useState(false);

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            holdreason_id: '',
            holdreason_text: e.target.value
        }));
    };

    const handleSelect = (reason) => {
        setFormData(prev => ({
            ...prev,
            holdreason_id: reason.value,
            holdreason_text: reason.key
        }));
        setShowOptions(false);
    };

    const displayValue = (() => {
        const matched = holdReasons.find(reason => String(reason.value) === String(formData.holdreason_id));
        return matched ? matched.key : formData.holdreason_text || '';
    })();

    return (
        <Form.Group className="position-relative">
            <Form.Label>Hold Reason</Form.Label>
            <Form.Control
                disabled ={Number(dataObj?.is_hold) === 1}
                type="text"
                placeholder="Type or select reason"
                value={displayValue}
                onChange={handleChange}
                onFocus={() => setShowOptions(true)}
                onBlur={() => setTimeout(() => setShowOptions(false), 200)}
            />

            {showOptions && (
                <div
                    className="dropdown-menu show w-100 mt-1"
                    style={{
                        maxHeight: '150px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                    }}
                >
                    {holdReasons.length > 0 ? (
                        holdReasons.map((reason, index) => (
                            <div
                                key={index}
                                className="dropdown-item"
                                style={{ cursor: 'pointer' }}
                                onMouseDown={() => handleSelect(reason)}
                            >
                                {reason.key}
                            </div>
                        ))
                    ) : (
                        <div className="dropdown-item text-muted">No suggestions</div>
                    )}
                </div>
            )}
        </Form.Group>
    );
};

const OrderHoldDetails = ({ dataObj, setRefreshOrder, handleClose }) => {
    const [formData, setFormData] = useState({
        is_hold: Number(dataObj?.is_hold) === 1 ? 1 : 0,
        holdreason_id: dataObj?.holdreason_id || '',
        holdreason_text: dataObj?.holdreason_text || '',
        deliverydatetime : dataObj.deliverydatetime,
        hold_qty: ''
    });
    const [holdQtyError, setHoldQtyError] = useState('');

    const holdReasonsList = JSON.parse(localStorage.getItem("activeHoldReasonsData") || "[]") || [];

    const handleSubmit = async (isHold) => {
        const orderId = dataObj?.orderid || dataObj?.order_id || dataObj?.id;
        const selectedHoldReason = holdReasonsList.find(reason => String(reason.value) === String(formData.holdreason_id));
        const holdReasonText = formData.holdreason_text || selectedHoldReason?.key || '';

        if (!orderId) {
            toast.error("Order ID is missing.");
            return;
        }

        if (isHold && !formData.holdreason_id && !holdReasonText.trim()) {
            toast.error("Hold reason is required.");
            return;
        }

        // Hold qty is optional here, but if it is filled in it must be valid.
        const holdQtyMessage = isHold ? validateHoldQty(formData.hold_qty, { required: false }) : '';
        if (holdQtyMessage) {
            setHoldQtyError(holdQtyMessage);
            toast.error(holdQtyMessage);
            return;
        }

        const sendingObj = isHold
            ? {
                order_id: orderId,
                is_hold: 1,
                holdreason_id: formData.holdreason_id ? Number(formData.holdreason_id) : 0,
                holdreason_text: holdReasonText,
                delivery_date : formData.deliverydatetime,
                // Left out when empty: an existing qty is kept, a new hold has none.
                ...(hasHoldQty(formData.hold_qty) ? { hold_qty: toHoldQty(formData.hold_qty) } : {})
            }
            : {
                order_id: orderId,
                is_hold: 0
            };

        let tokenStr = localStorage.getItem("accessToken");
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_SERVER_URL}/orders/update-order/`, sendingObj, {
                headers: { 'Authorization': `Bearer ${tokenStr}` }
            });

            if (data.status) {
                toast.success(data.message);
                if (setRefreshOrder) {
                    setRefreshOrder(prev => !prev);
                }
                handleClose(false);
            } else {
                toast.error(holdApiMessage(data.output) || holdApiMessage(data, "Getting some server error, please try again"));
            }
        } catch (error) {
            toast.error("Failed to update hold status.");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    return (
        <Card className='mt-2' style={{ height: 'calc(100vh - 560px)',}}>
            <Card.Body>
                <Form>
                    <Row >
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label>Rx Number</Form.Label>
                                <Form.Control type="text" value={dataObj?.rxnumber || dataObj?.rx_number || ''} disabled />
                            </Form.Group>
                        </Col>
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label>Hold Status</Form.Label>
                                <Form.Control type="text" value={Number(dataObj?.is_hold) === 1 ? 'On Hold' : 'Not On Hold'} disabled />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row >
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label>Patient</Form.Label>
                                <Form.Control type="text" value={dataObj?.patient || ''} disabled />
                            </Form.Group>
                        </Col>
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label>Drug</Form.Label>
                                <Form.Control type="text" value={dataObj?.drug || ''} disabled />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group>
                                <Form.Label>Delivery Date</Form.Label>
                                <DatePicker
                                    placeholderText="mm/dd/yy"
                                    className="form-control"
                                    disabled ={Number(dataObj?.is_hold) === 1}
                                    selected={formData.deliverydatetime ? moment(formData.deliverydatetime).format("MM/DD/YYYY") : null}
                                    onChange={(date) =>
                                        handleInputChange({ target: { name: 'deliverydatetime', value: moment(date).format("MM/DD/YYYY") } })
                                    }
                                    dateFormat="MM/dd/yy"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group>
                                <Form.Label>Hold Qty</Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="Optional"
                                    disabled ={Number(dataObj?.is_hold) === 1}
                                    value={Number(dataObj?.is_hold) === 1 ? formatHoldQty(dataObj?.hold_qty) : formData.hold_qty}
                                    isInvalid={Boolean(holdQtyError)}
                                    onChange={(e) => {
                                        if (isHoldQtyTyping(e.target.value)) {
                                            setFormData(prev => ({ ...prev, hold_qty: e.target.value }));
                                            setHoldQtyError('');
                                        }
                                    }}
                                />
                                <Form.Control.Feedback type="invalid">{holdQtyError}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={12} className="mb-3">
                            <HoldReasonInput
                                dataObj = {dataObj}
                                holdReasons={holdReasonsList}
                                formData={formData}
                                setFormData={setFormData}
                            />
                        </Col>
                    </Row>
                    {Number(dataObj?.is_hold) === 1 && (
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Hold By</Form.Label>
                                    <Form.Control type="text" value={dataObj?.hold_by_username || '-'} disabled />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Hold Date</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={dataObj?.hold_date ? moment(dataObj.hold_date).format('MM/DD/YY') : '-'}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Form>
            </Card.Body>
            <Card.Footer>
                <Row>
                    <Col className="text-end">
                        {Number(dataObj?.is_hold) === 1 ? (
                            <Button variant="outline-danger" className="me-2" onClick={() => handleSubmit(false)}>
                                Unhold
                            </Button>
                        ) :
                            <Button variant="success" onClick={() => handleSubmit(true)}>
                                {'Hold'}
                            </Button>
                        }
                    </Col>
                </Row>
            </Card.Footer>
        </Card>
    );
};

export default OrderHoldDetails;
